import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Home,
  User,
  Briefcase,
  Target,
  FileText,
  Calendar,
  Settings,
  LogOut,
  Menu,
  X,
  Lock
} from 'lucide-react';
import workAbroadService from '../../services/workAbroadService';
import { useAuth } from '../../context/AuthContext';
import logo from '../../assets/logo.png';

// Import section components
import WorkOverviewSection from '../../components/WorkAbroad/WorkOverviewSection';
import WorkPersonalDetailsSection from '../../components/WorkAbroad/WorkPersonalDetailsSection';
import WorkHistorySection from '../../components/WorkAbroad/WorkHistorySection';
import RolePreferencesSection from '../../components/WorkAbroad/RolePreferencesSection';
import WorkDocumentsSection from '../../components/WorkAbroad/WorkDocumentsSection';
import InterviewSection from '../../components/WorkAbroad/InterviewSection';
import WorkAccountSettingsSection from '../../components/WorkAbroad/WorkAccountSettingsSection';

const WorkDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [activeSection, setActiveSection] = useState('overview');
  const [profile, setProfile] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [profileData, overviewData] = await Promise.all([
        workAbroadService.getProfile(),
        workAbroadService.getOverview()
      ]);
      setProfile(profileData.data);
      setOverview(overviewData.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleNavigateToSection = (sectionId) => {
    setActiveSection(sectionId);
  };

  const menuItems = [
    {
      id: 'overview',
      label: 'Overview',
      icon: Home,
      locked: false,
      description: 'Your journey overview'
    },
    {
      id: 'personal-details',
      label: 'Personal Details',
      icon: User,
      locked: false,
      description: 'Basic information'
    },
    {
      id: 'work-history',
      label: 'Work History',
      icon: Briefcase,
      locked: false,
      description: 'Your experience'
    },
    {
      id: 'role-preferences',
      label: 'Role Preferences',
      icon: Target,
      locked: false,
      description: 'Preferred roles'
    },
    {
      id: 'documents',
      label: 'Upload Documents',
      icon: FileText,
      locked: !overview?.canAccessDocuments,
      description: overview?.canAccessDocuments 
        ? 'Required documents' 
        : 'Payment required'
    },
    {
      id: 'interview',
      label: 'Meet Employer',
      icon: Calendar,
      locked: !overview?.canAccessInterview,
      description: overview?.canAccessInterview 
        ? 'Interview scheduling' 
        : 'Payment required'
    },
    {
      id: 'account-settings',
      label: 'Account Settings',
      icon: Settings,
      locked: false,
      description: 'Manage your account'
    }
  ];

  const renderSection = () => {
    switch (activeSection) {
      case 'overview':
        return <WorkOverviewSection overview={overview} profile={profile} onRefresh={loadDashboardData} onNavigate={handleNavigateToSection} />;
      case 'personal-details':
        return <WorkPersonalDetailsSection profile={profile} onRefresh={loadDashboardData} />;
      case 'work-history':
        return <WorkHistorySection profile={profile} onRefresh={loadDashboardData} />;
      case 'role-preferences':
        return <RolePreferencesSection profile={profile} onRefresh={loadDashboardData} />;
      case 'documents':
        return <WorkDocumentsSection profile={profile} onRefresh={loadDashboardData} />;
      case 'interview':
        return <InterviewSection profile={profile} onRefresh={loadDashboardData} />;
      case 'account-settings':
        return <WorkAccountSettingsSection profile={profile} onRefresh={loadDashboardData} onNavigate={handleNavigateToSection} />;
      default:
        return <WorkOverviewSection overview={overview} profile={profile} onRefresh={loadDashboardData} onNavigate={handleNavigateToSection} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F5F8FA] to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202356] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gradient-to-br from-[#F5F8FA] to-white flex flex-col">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <img src={logo} alt="Notapaths" className="h-8" />
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <motion.aside
          initial={false}
          animate={{
            x: sidebarOpen ? 0 : -320,
            opacity: sidebarOpen ? 1 : 0
          }}
          className={`fixed lg:static lg:h-full inset-y-0 left-0 z-40 w-80 bg-white border-r border-gray-200 overflow-y-auto flex flex-col ${
            sidebarOpen ? 'block' : 'hidden lg:block'
          }`}
        >
          {/* Sidebar Header */}
          <div className="p-6 border-b border-gray-200">
            <img src={logo} alt="Notapaths" className="h-10 mb-2" />
            <p className="text-sm text-gray-600 mt-1">Your pathway to global employment</p>
          </div>

          {/* Progress Bar */}
          {overview && (
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Overall Progress</span>
                <span className="text-sm font-semibold text-[#202356]">
                  {overview.progressPercentage}%
                </span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${overview.progressPercentage}%` }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="h-full bg-[#202356] rounded-full"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {profile?.paymentStatus === 'paid' 
                  ? 'All sections unlocked' 
                  : 'Complete free sections to proceed'}
              </p>
            </div>
          )}

          {/* Navigation Menu */}
          <nav className="p-4 flex-1">
            <ul className="space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                const isLocked = item.locked;

                return (
                  <li key={item.id}>
                    <button
                      onClick={() => {
                        if (!isLocked) {
                          setActiveSection(item.id);
                          if (window.innerWidth < 1024) {
                            setSidebarOpen(false);
                          }
                        }
                      }}
                      disabled={isLocked}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                        isActive
                          ? 'bg-[#202356] text-white shadow-md'
                          : isLocked
                          ? 'text-gray-400 cursor-not-allowed hover:bg-gray-50'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Icon size={20} />
                      <div className="flex-1 text-left">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{item.label}</span>
                          {isLocked && <Lock size={14} />}
                        </div>
                        <p className="text-xs opacity-75 mt-0.5">{item.description}</p>
                      </div>
                      {overview?.sectionsCompleted?.[item.id.replace(/-/g, '')] && (
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Logout Button */}
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut size={20} />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </motion.aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto">
          {renderSection()}
        </main>
      </div>
    </div>
  );
};

export default WorkDashboard;
